/**
 * Phone DISPLAY. The normalising half moved to `@crm/contracts` so the browser
 * and the server agree on what a number is — they both have to match the same
 * stored value, and two implementations would eventually disagree on a leading
 * zero and create a duplicate contact nobody could explain.
 */
import { DEFAULT_DIALLING_CODE, isValidE164, NATIONAL_NUMBER_LENGTHS, normalisePhone } from '@crm/contracts';

export { DEFAULT_DIALLING_CODE, isValidE164, normalisePhone };

/**
 * Readable grouping for display only. Never store this — the column holds E.164.
 *
 * Indian mobiles group 5+5, the convention people actually read. Everything
 * else groups in threes with any trailing one- or two-digit orphan folded back
 * into the previous group, so "+1 415 555 0132" never renders as
 * "+1 415 555 013 2". This is presentation, not a claim about a country's real
 * numbering plan — nothing parses it back.
 */
export function formatPhoneDisplay(e164: string | null | undefined): string {
  // Nullish is a real state now — a customer may arrive as a name alone — and
  // every caller that renders a number would otherwise need the same guard.
  if (!e164) return '';
  if (!isValidE164(e164)) return e164;
  const digits = e164.slice(1);

  if (digits.startsWith(DEFAULT_DIALLING_CODE) && digits.length === 12) {
    const local = digits.slice(2);
    return `+${DEFAULT_DIALLING_CODE} ${local.slice(0, 5)} ${local.slice(5)}`;
  }

  /*
   * A country code we recognise wins over the guess below.
   *
   * Without this, a stored "+9187878883" — which the old normaliser could
   * produce — rendered as "+9 187 878 883", inventing a country code of 9 and
   * making a wrong number look like a foreign one rather than a mistake.
   */
  const known = Object.keys(NATIONAL_NUMBER_LENGTHS).find((c) => digits.startsWith(c));
  if (known) {
    const local = digits.slice(known.length);
    const parts = local.match(/.{1,5}/g) ?? [];
    return `+${known} ${parts.join(' ')}`.trim();
  }

  // Assume a 10-digit national number where we can; otherwise a 1-digit code.
  const code = digits.length > 10 ? digits.slice(0, digits.length - 10) : digits.slice(0, 1);
  const national = digits.slice(code.length);

  const groups = national.match(/.{1,3}/g) ?? [];
  if (groups.length > 1 && groups[groups.length - 1].length < 3) {
    groups[groups.length - 2] += groups.pop();
  }
  return `+${code} ${groups.join(' ')}`.trim();
}
